// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { FormField, FormState } from "@keller/XDL/Utilities/form";

export const createAccountOptionValues = {
    existingAccount: "Align to Existing Keller Account",
    newAccount: "Apply for New Account",
};
export interface AccountFormState extends FormState {
    fields: {
        defaultWarehouseId: FormField<string>;
        firstName: FormField<string>;
        lastName: FormField<string>;
        email: FormField<string>;
        userName: FormField<string>;
        phone: FormField<string>;
        password: FormField<string>;
        confirmPassword: FormField<string>;
        applyForPurchasable: FormField<boolean>;
        createAccountOption: FormField<"Align to Existing Keller Account" | "Apply for New Account">;
        role: FormField<"admin" | "buyer" | "">;
        adminName: FormField<string>;
        adminEmail: FormField<string>;
        accountType: FormField<"cashAccount" | "creditAccount" | "both">;
        customerAccounts: FormField<string>;
        industry: FormField<string>;
        companyName: FormField<string>;
        address1: FormField<string>;
        address2: FormField<string>;
        country: FormField<string>;
        city: FormField<string>;
        state: FormField<string>;
        zip: FormField<string>;
        notes: FormField<string>;
        stayInTouchByPrint: FormField<boolean>;
        stayInTouchByOnline: FormField<boolean>;
        creditApplicationFile: FormField<any>;
    };
}

export const initialAccountFormState: AccountFormState = {
    isValid: false,
    isSubmitted: false,
    fields: {
        defaultWarehouseId: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_branch",
                placeholder: translate("Branch"),
                label: translate("Branch"),
                required: true,
                name: "defaultWarehouseId",
            },
        },
        firstName: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_firstName",
                name: "firstName",
                label: translate("First Name"),
                placeholder: translate("Your First Name"),
                required: true,
                maxLength: 40,
            },
        },
        lastName: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "createAccount_lastName",
                name: "lastName",
                label: translate("Last Name"),
                placeholder: translate("Your Last Name"),
                required: true,
                maxLength: 40,
            },
        },
        email: {
            value: "",
            validators: ["required", "email"],
            attrs: {
                type: "email",
                name: "email",
                id: "createAccount_email",
                label: translate("Email Address"),
                placeholder: translate("Your Email"),
                required: true,
                maxLength: 256,
            },
        },
        userName: {
            value: "",
            validators: (state, options) => {
                return !options?.accountSettings?.useEmailAsUserName ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_userName",
                name: "userName",
                label: translate("User Name"),
                placeholder: translate("Your User Name"),
                required: true,
            },
        },
        phone: {
            value: "",
            validators: ["required", "phone"],
            attrs: {
                id: "createAccount_phoneNumber",
                name: "phone",
                label: translate("Phone Number"),
                placeholder: "( ___ ) ___ - ____",
                required: true,
                maxLength: 14,
            },
        },
        password: {
            value: "",
            validators: ["required", "password"],
            validatorsOptions: state => {
                return { confirmPassword: state.fields.confirmPassword.value };
            },
            attrs: {
                id: "createAccount_password",
                name: "password",
                label: translate("Password"),
                placeholder: translate("Password"),
                required: true,
            },
        },
        confirmPassword: {
            value: "",
            validators: ["required", "confirmPassword"],
            validatorsOptions: state => {
                return { password: state.fields.password.value };
            },
            attrs: {
                id: "createAccount_confirmPassword",
                name: "confirmPassword",
                label: translate("Confirm Password"),
                placeholder: translate("Confirm Password"),
                required: true,
            },
        },
        applyForPurchasable: {
            value: false,
            validators: [],
            attrs: {
                id: "createAccount_applyForPurchasable",
                name: "applyForPurchasable",
                label: translate("Confirm Password"),
                placeholder: translate("Confirm Password"),
                required: true,
            },
        },
        createAccountOption: {
            value: "Align to Existing Keller Account",
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_haveAccount",
                name: "createAccountOption",
                required: true,
            },
        },
        role: {
            value: "",
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_role",
                name: "role",
                label: translate("Desired Role"),
                placeholder: translate("Desired Role"),
                required: true,
            },
        },
        adminName: {
            value: "",
            validators: [],
            attrs: {
                id: "createAccount_adminName",
                name: "adminName",
                label: translate("Admin Name"),
                placeholder: translate("Admin Name"),
            },
        },
        adminEmail: {
            value: "",
            validators: [],
            attrs: {
                id: "createAccount_adminEmail",
                name: "adminEmail",
                label: translate("Admin Email"),
                placeholder: translate("Admin Email"),
            },
        },
        accountType: {
            value: "cashAccount",
            validators: state => {
                return state.fields.createAccountOption.value === createAccountOptionValues.existingAccount &&
                    state.fields.applyForPurchasable.value
                    ? ["required"]
                    : [];
            },
            attrs: {
                id: "createAccount_accountNumberType",
                name: "accountType",
                label: translate("Do you have an account number with Keller?"),
                required: true,
            },
        },
        customerAccounts: {
            value: "",
            validators: state => {
                return state.fields.createAccountOption.value === createAccountOptionValues.existingAccount &&
                    state.fields.applyForPurchasable.value
                    ? ["required"]
                    : [];
            },
            attrs: {
                id: "createAccount_customerNumbers",
                name: "customerAccounts",
                label: translate("Please list the existing accounts you wish to have access to on our new site"),
                placeholder: translate("Customer Account Number(s)"),
                required: true,
            },
        },
        industry: {
            value: "",
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_industry",
                name: "industry",
                label: translate("Industry"),
                required: true,
            },
        },
        companyName: {
            value: "",
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_companyName",
                name: "companyName",
                label: translate("Company Name"),
                placeholder: translate("Your Company Name"),
                required: true,
            },
        },
        address1: {
            value: "",
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_address1",
                name: "address1",
                label: translate("Address1"),
                placeholder: translate("Your First Address"),
                required: true,
            },
        },
        address2: {
            value: "",
            validators: [],
            attrs: {
                id: "createAccount_address2",
                name: "address2",
                label: translate("Address2"),
                placeholder: translate("Your Second Address"),
            },
        },
        country: {
            value: "",
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_country",
                name: "country",
                label: translate("Country"),
                required: true,
            },
        },
        state: {
            value: "",
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_state",
                name: "state",
                label: translate("State"),
                required: true,
            },
        },
        city: {
            value: "",
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_city",
                name: "city",
                label: translate("City"),
                placeholder: translate("Your City"),
                required: true,
            },
        },
        zip: {
            value: "",
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["required"] : [];
            },
            attrs: {
                id: "createAccount_zip",
                name: "zip",
                label: translate("Zip"),
                placeholder: translate("Zip Code"),
                required: true,
            },
        },
        notes: {
            value: "",
            validators: [],
            attrs: {
                id: "createAccount_notes",
                name: "notes",
                label: translate("Notes"),
                placeholder: translate("Notes"),
                maxLength: 500,
            },
        },
        stayInTouchByPrint: {
            value: false,
            validators: [],
            attrs: {
                id: "createAccount_stayInTouch_byPrint",
                name: "stayInTouchByPrint",
            },
        },
        stayInTouchByOnline: {
            value: false,
            validators: [],
            attrs: {
                id: "createAccount_stayInTouch_byOnline",
                name: "stayInTouchByOnline",
            },
        },
        creditApplicationFile: {
            value: null,
            validators: state => {
                return state.fields.applyForPurchasable.value ? ["file"] : [];
            },
            attrs: {
                name: "creditApplicationFile",
            },
        },
    },
};
